import { useMemo } from "react";

import { useIDPResults } from "../../../api/hooks/idpFd/useIDPResults";
import {
  DateRangeFilterInput,
  DocType,
  IdpFdDocumentsFilter,
  IdpFdDocumentsOrderBy,
  OrderBy,
  Status,
} from "../../../graphql/generated";
import { DocumentType } from "../../IDPForgeryCheck/types";
import {
  mapDocTypeToDocumentType,
  mapDocumentTypeToDocType,
} from "../../IDPForgeryCheck/utils";
import {
  DocumentDatabaseEntry,
  DocumentDatabaseResponse,
  DocumentDatabaseStatus,
} from "../types";

interface UseIDPDatabaseDataProps {
  page?: number;
  limit?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  filters?: {
    dateRange?: DateRangeFilterInput;
    documentType?: string | null;
    customerId?: string | null;
  };
}

const mapStatusToDocumentDatabaseStatus = (
  status: Status
): DocumentDatabaseStatus => {
  switch (status) {
    case Status.Completed:
      return DocumentDatabaseStatus.AVAILABLE;
    case Status.Failed:
      return DocumentDatabaseStatus.FAILED;
    case Status.Ongoing:
      return DocumentDatabaseStatus.PROCESSING;
    case Status.Rejected:
      return DocumentDatabaseStatus.UNREADABLE;
    default:
      return DocumentDatabaseStatus.PROCESSING;
  }
};

const mapDocumentTypeToDocTypeFilter = (
  documentType: string
): DocType | undefined => {
  try {
    return mapDocumentTypeToDocType(documentType as DocumentType);
  } catch {
    return undefined;
  }
};

export const useIDPDatabaseData = ({
  page = 1,
  limit = 20,
  sortField = "createdAt",
  sortDirection = "desc",
  filters,
}: UseIDPDatabaseDataProps = {}) => {
  const filter: IdpFdDocumentsFilter = useMemo(() => {
    const filterObj: IdpFdDocumentsFilter = {};

    if (filters?.dateRange) {
      filterObj.createdAt = filters.dateRange;
    }

    if (filters?.documentType) {
      const mappedDocType = mapDocumentTypeToDocTypeFilter(
        filters.documentType
      );
      if (mappedDocType) {
        filterObj.documentType = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          equals: mappedDocType,
        };
      }
    }

    if (filters?.customerId) {
      filterObj.customer = {
        id: {
          equals: Number(filters.customerId),
        },
      };
    }

    return filterObj;
  }, [filters]);

  const orderBy: IdpFdDocumentsOrderBy = useMemo(() => {
    const direction = sortDirection === "asc" ? OrderBy.Asc : OrderBy.Desc;

    switch (sortField) {
      case "requestDate":
      case "createdAt":
        return { createdAt: direction };
      case "documentType":
        return { documentType: direction };
      case "name":
        return { customer: { name: direction } };
      case "companyName":
        return { customer: { name: direction } };
      case "status":
        return { status: direction };
      default:
        return { createdAt: direction };
    }
  }, [sortField, sortDirection]);

  const [{ data: idpResponse, fetching: loading, error }] = useIDPResults({
    page,
    filter,
    orderBy,
  });

  const data: DocumentDatabaseResponse | null = useMemo(() => {
    if (!idpResponse?.idpFdDocuments) return null;

    const apiData = idpResponse.idpFdDocuments;

    const transformedData: DocumentDatabaseEntry[] = apiData.data.map(
      (item) => ({
        id: item.id,
        requestDate: item.createdAt || new Date().toISOString(),
        documentType: mapDocTypeToDocumentType(item.documentType),
        companyName: item.customer?.name || "Unknown Company",
        status: mapStatusToDocumentDatabaseStatus(item.status),
        fileUrl: item.document?.url || undefined,
        fileName: item.document?.filePath
          ? `document_${item.document.filePath}.pdf`
          : undefined,
        fileSize: undefined,
        processedAt: item.isCompleted ? item.updatedAt : undefined,
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString(),
        sector: item.customer.sector ?? "",
        subSector: item.customer.subSector ?? "",
      })
    );

    return {
      data: transformedData,
      total: apiData.total,
      page,
      limit,
    };
  }, [idpResponse, page, limit]);

  return {
    data,
    loading,
    error,
  };
};
